import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    makeStyles,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    Theme,
    Typography,
} from '@material-ui/core'
import DescriptionIcon from '@material-ui/icons/Description'
import { OrganizationDocumentType } from '../../models/Company/types'

const useStyles = makeStyles((theme: Theme) => ({
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    fileList: {
        maxHeight: 96,
        overflowY: 'auto',
        padding: theme.spacing(1.25, 1.5),
        marginBottom: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.grey[50],
    },
    fileName: {
        fontSize: 13,
        color: theme.palette.text.secondary,
    },
    optionGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(2),
    },
    optionCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        transition: theme.transitions.create(['border-color', 'box-shadow']),
    },
    selectedOptionCard: {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
    optionBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        paddingLeft: theme.spacing(4),
    },
    typeSelect: {
        marginTop: theme.spacing(1),
    },
}))

export type DocumentUploadChoice =
    | { kind: 'general' }
    | { kind: 'typed'; documentTypeId: number }

export interface DocumentUploadKindDialogProps {
    open: boolean
    onClose: () => void
    files: File[]
    /** Active catalog types (caller filters to `is_active`). */
    documentTypes: OrganizationDocumentType[]
    onChoose: (choice: DocumentUploadChoice) => void
}

export const DocumentUploadKindDialog: React.FC<DocumentUploadKindDialogProps> = ({
    open,
    onClose,
    files,
    documentTypes,
    onChoose,
}) => {
    const classes = useStyles()

    const activeTypes = documentTypes.filter((t) => t.is_active)
    const firstTypeId = activeTypes[0]?.id ?? null
    const [choiceKind, setChoiceKind] = useState<'general' | 'typed'>('general')
    const [selectedTypeId, setSelectedTypeId] = useState<number | null>(
        firstTypeId,
    )

    useEffect(() => {
        if (!open) return
        setChoiceKind('general')
        setSelectedTypeId(firstTypeId)
    }, [firstTypeId, open])

    const selectedType = useMemo(
        () => activeTypes.find((t) => t.id === selectedTypeId) ?? null,
        [activeTypes, selectedTypeId],
    )

    const handleSubmit = () => {
        if (choiceKind === 'general') {
            onChoose({ kind: 'general' })
            return
        }
        if (selectedTypeId != null) {
            onChoose({ kind: 'typed', documentTypeId: selectedTypeId })
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle disableTypography>
                <Box className={classes.title}>
                    <DescriptionIcon color="primary" />
                    <Typography variant="h6" component="span">
                        Choose document category
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                {files.length > 0 && (
                    <Box className={classes.fileList}>
                        <Typography variant="caption" color="textSecondary">
                            {files.length} file{files.length === 1 ? '' : 's'}
                        </Typography>
                        {files.map((f, i) => (
                            <Typography
                                key={`${f.name}-${i}`}
                                className={classes.fileName}
                                noWrap
                            >
                                {f.name}
                            </Typography>
                        ))}
                    </Box>
                )}

                <Typography variant="body2" color="textSecondary" paragraph>
                    Upload as a general document, or file it under an
                    organization document type category.
                </Typography>

                <RadioGroup
                    value={choiceKind}
                    onChange={(e) =>
                        setChoiceKind(e.target.value as 'general' | 'typed')
                    }
                    className={classes.optionGroup}
                >
                    <Box
                        className={`${classes.optionCard} ${
                            choiceKind === 'general'
                                ? classes.selectedOptionCard
                                : ''
                        }`}
                    >
                        <FormControlLabel
                            value="general"
                            control={<Radio color="primary" />}
                            label={
                                <Box>
                                    <Typography variant="subtitle2">
                                        General document
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Store this file without tying it to a
                                        document type category.
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>

                    <Box
                        className={`${classes.optionCard} ${
                            choiceKind === 'typed'
                                ? classes.selectedOptionCard
                                : ''
                        }`}
                    >
                        <FormControlLabel
                            value="typed"
                            control={<Radio color="primary" />}
                            disabled={activeTypes.length === 0}
                            label={
                                <Box>
                                    <Typography variant="subtitle2">
                                        Document type
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Assign this upload to a type from your
                                        organization catalog. Multiple documents
                                        can use the same type.
                                    </Typography>
                                </Box>
                            }
                        />

                        <Box className={classes.optionBody}>
                            {activeTypes.length === 0 ? (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    No active document types are available.
                                </Typography>
                            ) : (
                                <>
                                    <TextField
                                        className={classes.typeSelect}
                                        label="Document type"
                                        variant="outlined"
                                        size="small"
                                        select
                                        fullWidth
                                        value={selectedTypeId ?? ''}
                                        onChange={(e) => {
                                            setChoiceKind('typed')
                                            setSelectedTypeId(
                                                Number(e.target.value),
                                            )
                                        }}
                                        disabled={choiceKind !== 'typed'}
                                    >
                                        {activeTypes.map((t) => (
                                            <MenuItem key={t.id} value={t.id}>
                                                {t.name}
                                                {t.is_required_for_current_company
                                                    ? ' (required)'
                                                    : ''}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    {selectedType?.description && (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            {selectedType.description}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>
                </RadioGroup>
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button onClick={onClose} color="default">
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                    disabled={choiceKind === 'typed' && selectedTypeId == null}
                >
                    Continue
                </Button>
            </DialogActions>
        </Dialog>
    )
}
